import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { notifySuccess } from 'common/utils/notifier.utils';
import { ELimitsPeriod } from 'domains/limits/enums/limits.enum';
import {
  useSetLimitsCustomerAmountMutation,
  useSetLimitsCustomerPaymentMutation,
} from 'domains/limits/queries/limits.query';
import {
  SBottomButtonsWrapper,
  SFormAmountWrapper,
  SInfoField,
  SInfoFieldLabel,
  SInfoFieldValue,
} from './modal.styles';
import { ETableRowType } from '../enums/individual-limits.enums';
import { TIndividualLimitsModalData } from '../types/individual-limits.types';

enum EFormFields {
  Days = 'days',
  Min = 'min',
  Max = 'max',
}

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TIndividualLimitsModalData;
  invalidateDataTableHandler: () => Promise<void>;
};

const SetLimitsModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    invalidateDataTableHandler,
    data: {
      type,
      accountTypeGuid,
      counterpartyLabel,
      counterpartyGUID,
      // segmentLabel,
      userName,
      typeLabel,
      periodLabel,
      periodKey,
      maxAmount,
      minAmount,
      amountPeriodKey,
      assetCode,
      assetIssuer,
      customerGUID,
    },
  } = props;
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  const { mutateAsync: mutateAsyncSetLimitsCustomerAmountMutation } = useSetLimitsCustomerAmountMutation();

  const { mutateAsync: mutateAsyncSetLimitsCustomerPaymentMutation } = useSetLimitsCustomerPaymentMutation();

  useEffect(() => {
    form.setFields([
      {
        name: EFormFields.Days,
        value: amountPeriodKey,
      },
      {
        name: EFormFields.Min,
        value: minAmount,
      },
      {
        name: EFormFields.Max,
        value: maxAmount,
      },
    ]);
  }, [minAmount, maxAmount]);

  const onFinishFormHandler = async (data: Record<EFormFields, string>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);

      const { min, max, days } = data;

      if (type === ETableRowType.User) {
        const limitsUsersReqData = {
          account_type_guid: accountTypeGuid,
          asset_code: assetCode,
          asset_issuer: assetIssuer,
          customer_guid: customerGUID,
          max: Number(max),
          period: periodKey as ELimitsPeriod,
          period_key: Number(days) || (amountPeriodKey as number),
        };
        await mutateAsyncSetLimitsCustomerAmountMutation({
          counterpartyGUID,
          requestData: limitsUsersReqData,
        });
      } else if (type === ETableRowType.Operation) {
        const limitsOperationsReqData = {
          account_type_guid: accountTypeGuid,
          asset_code: assetCode,
          asset_issuer: assetIssuer,
          customer_guid: customerGUID,
          max: Number(max),
          min: Number(min),
        };
        await mutateAsyncSetLimitsCustomerPaymentMutation({
          counterpartyGUID,
          requestData: limitsOperationsReqData,
        });
      }

      await invalidateDataTableHandler();

      notifySuccess('limits.notification.success');

      setIsSubmitBtnLoading(false);
      onCancelModalHandler();
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  return (
    <EnhancedModal
      open={isOpen}
      onCancel={onCancelModalHandler}
      width="384px"
    >
      <Form
        form={form}
        onFinish={onFinishFormHandler}
      >
      <Header>{t('limits.individualLimits.modal.edit.title')}</Header>
      <Body>
        <SInfoField>
          <SInfoFieldLabel>{t('limits.individualLimits.modal.edit.fields.label.counterparty')}</SInfoFieldLabel>
          <SInfoFieldValue>{counterpartyLabel}</SInfoFieldValue>
        </SInfoField>
        {type !== ETableRowType.Operation && (
          <SInfoField>
            <SInfoFieldLabel>{t('limits.individualLimits.modal.edit.fields.label.period')}</SInfoFieldLabel>
            <SInfoFieldValue>{periodLabel}</SInfoFieldValue>
          </SInfoField>
        )}
        {/* <SInfoField> */}
        {/*   <SInfoFieldLabel>{t('limits.individualLimits.modal.edit.fields.label.segment')}</SInfoFieldLabel> */}
        {/*   <SInfoFieldValue>{segmentLabel}</SInfoFieldValue> */}
        {/* </SInfoField> */}
        <SInfoField>
          <SInfoFieldLabel>{t('limits.individualLimits.modal.edit.fields.label.type')}</SInfoFieldLabel>
          <SInfoFieldValue>{typeLabel}</SInfoFieldValue>
        </SInfoField>
        <SInfoField>
          <SInfoFieldLabel>{t('limits.individualLimits.modal.edit.fields.label.userName')}</SInfoFieldLabel>
          <SInfoFieldValue>{userName}</SInfoFieldValue>
        </SInfoField>
        {/* <SInfoField> */}
        {/*   <SInfoFieldLabel> */}
        {/*     {t('limits.individualLimits.modal.edit.fields.label.accountNumber')} */}
        {/*   </SInfoFieldLabel> */}
        {/*   <SInfoFieldValue>{accountTypeGuid}</SInfoFieldValue> */}
        {/* </SInfoField> */}
        <SInfoField>
          <SInfoFieldLabel>{t('limits.individualLimits.modal.edit.fields.label.currency')}</SInfoFieldLabel>
          <SInfoFieldValue>{assetCode}</SInfoFieldValue>
        </SInfoField>
        {periodKey === ELimitsPeriod.Days && (
          <Form.Item
            name={EFormFields.Days}
            label={t('limits.individualLimits.modal.edit.fields.label.days')}
          >
            <Input suffix={t('limits.individualLimits.modal.edit.fields.suffix.days')} />
          </Form.Item>
        )}
        <SFormAmountWrapper>
          <Form.Item
            name={EFormFields.Min}
            label={t('limits.individualLimits.modal.edit.fields.label.min')}
          >
            <Input
              disabled={type !== ETableRowType.Operation}
              suffix={assetCode}
            />
          </Form.Item>
          <Form.Item
            name={EFormFields.Max}
            label={t('limits.individualLimits.modal.edit.fields.label.max')}
          >
            <Input suffix={assetCode} />
          </Form.Item>
        </SFormAmountWrapper>
      </Body>
      <Footer>
        <SBottomButtonsWrapper>
          <Button onClick={onCancelModalHandler}>
            {t('limits.individualLimits.modal.edit.button.cancel')}
          </Button>
          <Button
            loading={isSubmitBtnLoading}
            htmlType="submit"
            type="primary"
          >
            {t('limits.individualLimits.modal.edit.button.submit')}
          </Button>
        </SBottomButtonsWrapper>
      </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default SetLimitsModal;
